import { useMemo, useState, useEffect, useRef, Fragment } from "react";
import { CaretDown, CaretUp, CaretRight, MagnifyingGlass, CalendarBlank, X } from "@phosphor-icons/react";

/**
 * Reusable sortable + searchable data grid.
 *
 * Props:
 *  - columns: [{ key, header, accessor?, render?, align?, sortable?, width? }]
 *      accessor(row) -> raw value used for sorting/search; defaults to row[key]
 *      render(row)   -> JSX cell content; defaults to accessor value
 *      sortable      -> false to disable sorting on a column
 *      align         -> "left" (default) | "right" | "center"
 *  - rows: array
 *  - getRowKey: (row) => unique id
 *  - onRowClick: (row) => void (optional)
 *  - defaultSort: { key, dir } (optional)
 *  - searchPlaceholder, statusBar(visibleRows, allRows), emptyMessage
 *  - testid: base data-testid; rows get `${testid}-row-${key}`, search gets `${testid}-search`
 */
export default function SortableGrid({
  columns,
  rows,
  getRowKey,
  onRowClick,
  defaultSort,
  searchPlaceholder = "Search…",
  statusBar,
  emptyMessage,
  testid,
  toolbarRight,
  renderExpanded,
  getRowClassName,
  pageSize,
  getRowHref,      // (row) => url — enables Ctrl/Cmd/middle-click to open in a new tab
  highlightKey,    // key of the row to highlight + scroll into view (e.g. the last-visited row)
  persistKey,      // sessionStorage namespace to remember search/sort/page across navigation
  onFilteredRowsChange, // (rows) => void — reports the fully filtered+sorted rows (pre-pagination)
                        // so a parent "Export" button can download exactly what's shown
}) {
  const _ps = persistKey ? (() => {
    try { return JSON.parse(sessionStorage.getItem(`grid:${persistKey}`) || "null") || {}; }
    catch { return {}; }
  })() : {};
  const [sort, setSort] = useState(_ps.sort || defaultSort || { key: null, dir: "asc" });
  const [q, setQ] = useState(_ps.q || "");
  // Debounce the term that drives filtering so typing stays snappy on large lists (the input
  // updates instantly via `q`; the expensive filter/re-render only runs ~220ms after you pause).
  const [debouncedQ, setDebouncedQ] = useState(_ps.q || "");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 220);
    return () => clearTimeout(t);
  }, [q]);
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpand = (k) => setExpanded((s) => {
    const n = new Set(s);
    n.has(k) ? n.delete(k) : n.add(k);
    return n;
  });

  const dateCols = useMemo(() => columns.filter((c) => c.type === "date"), [columns]);
  const [dateField, setDateField] = useState(() => (dateCols[0]?.key || ""));
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const toMillis = (v) => (v == null || v === "" ? NaN : (v instanceof Date ? v.getTime() : Date.parse(v)));

  const filtered = useMemo(() => {
    let out = rows;
    if (debouncedQ.trim()) {
      const s = debouncedQ.toLowerCase();
      out = out.filter((r) =>
        columns.some((c) => {
          // A column may expose extra text for search (searchAccessor) distinct from its
          // sort value (accessor) — e.g. a "Customer" column that also matches container/invoice #.
          const v = c.searchAccessor ? c.searchAccessor(r) : (c.accessor ? c.accessor(r) : r[c.key]);
          return v != null && String(v).toLowerCase().includes(s);
        })
      );
    }
    if (dateField && (dateFrom || dateTo)) {
      const col = columns.find((c) => c.key === dateField);
      const acc = col ? (col.accessor || ((r) => r[col.key])) : null;
      const fromMs = dateFrom ? Date.parse(dateFrom + "T00:00:00") : -Infinity;
      const toMs = dateTo ? Date.parse(dateTo + "T23:59:59") : Infinity;
      if (acc) {
        out = out.filter((r) => {
          const ms = toMillis(acc(r));
          if (isNaN(ms)) return false;
          return ms >= fromMs && ms <= toMs;
        });
      }
    }
    return out;
  }, [rows, debouncedQ, columns, dateField, dateFrom, dateTo]);

  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const acc = col.accessor || ((r) => r[sort.key]);
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = acc(a);
      const vb = acc(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      const da = Date.parse(va);
      const db = Date.parse(vb);
      if (!isNaN(da) && !isNaN(db) && typeof va === "string" && typeof vb === "string"
          && /^\d{4}-\d{2}-\d{2}/.test(va) && /^\d{4}-\d{2}-\d{2}/.test(vb)) {
        return (da - db) * dir;
      }
      return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
    });
  }, [filtered, sort, columns]);

  // Report the filtered+sorted rows (before pagination) so a parent export can match the view.
  // Guard against an infinite render loop: parents often pass a fresh `columns` array literal each
  // render, which makes `sorted` a new array every time even when the underlying data is unchanged.
  // Calling the parent's setter unconditionally would re-render the parent → new columns → new
  // sorted → setter again ("Maximum update depth exceeded"), which also thrashes the row DOM and
  // breaks taps on mobile. So only notify when the actual row set changes (by element reference).
  const _lastSentRef = useRef(null);
  useEffect(() => {
    if (!onFilteredRowsChange) return;
    const prev = _lastSentRef.current;
    const same = prev && prev.length === sorted.length && prev.every((x, i) => x === sorted[i]);
    if (same) return;
    _lastSentRef.current = sorted;
    onFilteredRowsChange(sorted);
  }, [sorted, onFilteredRowsChange]);

  // Opt-in pagination: only render one page of rows to keep large tables (e.g. 1000+ shipments)
  // snappy. When pageSize is undefined the full list renders (unchanged behaviour).
  const [page, setPage] = useState(_ps.page || 0);
  const _firstReset = useRef(true);
  useEffect(() => {
    // Don't clobber a restored page on the very first render (persistKey/highlight restore).
    if (_firstReset.current) { _firstReset.current = false; return; }
    setPage(0);
  }, [debouncedQ, sort, dateFrom, dateTo, dateField, pageSize]);
  const pageCount = pageSize ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  const pageSafe = Math.min(page, pageCount - 1);
  const visible = useMemo(
    () => (pageSize ? sorted.slice(pageSafe * pageSize, pageSafe * pageSize + pageSize) : sorted),
    [sorted, pageSize, pageSafe]);

  // Remember search / sort / page across navigation so the list looks the same on Back.
  useEffect(() => {
    if (!persistKey) return;
    try { sessionStorage.setItem(`grid:${persistKey}`, JSON.stringify({ q, sort, page: pageSafe })); }
    catch { /* ignore quota / private-mode */ }
  }, [persistKey, q, sort, pageSafe]);

  // Jump to the page holding the highlighted row (e.g. the one just visited) and scroll to it.
  const _hlRef = useRef(null);
  const _hlDone = useRef(false);

  // Touch tap tracking. Rows live inside a horizontally-scrollable container, so on real
  // touch devices a finger tap with tiny movement is often treated as a pan and the synthetic
  // `click` never fires (desktop mouse clicks are unaffected). We detect a genuine tap from the
  // raw touch events and trigger the row action ourselves, suppressing the ghost click so it
  // doesn't navigate twice.
  const _touchRef = useRef(null);
  const _suppressClickRef = useRef(false);
  useEffect(() => {
    if (_hlDone.current || highlightKey == null) return;
    const idx = sorted.findIndex((r) => String(getRowKey(r)) === String(highlightKey));
    if (idx < 0) return;
    _hlDone.current = true;
    if (pageSize) setPage(Math.floor(idx / pageSize));
  }, [highlightKey, sorted, pageSize, getRowKey]);
  useEffect(() => {
    if (highlightKey == null || !_hlRef.current) return;
    const t = setTimeout(() => _hlRef.current?.scrollIntoView({ block: "center", behavior: "smooth" }), 60);
    return () => clearTimeout(t);
  }, [highlightKey, visible]);

  // Row activation: modifier / middle click opens the detail in a new tab (like a real link).
  const activateRow = (e, r) => {
    const href = getRowHref ? getRowHref(r) : null;
    if (href && (e.metaKey || e.ctrlKey || e.button === 1)) {
      e.preventDefault();
      window.open(href, "_blank", "noopener");
      return true;
    }
    return false;
  };

  const toggle = (col) => {
    if (col.sortable === false) return;
    setSort((s) => (s.key === col.key ? { key: col.key, dir: s.dir === "asc" ? "desc" : "asc" } : { key: col.key, dir: "asc" }));
  };

  const clearDates = () => { setDateFrom(""); setDateTo(""); };
  const dateActive = dateField && (dateFrom || dateTo);

  return (
    <>
      <style>{`
        tr.grid-row-highlight > td { background: var(--omh-highlight, #FEF9C3) !important; }
        tr.grid-row-highlight > td:first-child { box-shadow: inset 3px 0 0 var(--omh-primary, #2A5C9B) !important; }
      `}</style>
      <div style={{
        display: "flex", gap: 10, alignItems: "center",
        padding: "10px 12px", borderBottom: "1px solid var(--omh-divider)",
        background: "var(--omh-bg-subtle)", flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flex: "0 0 auto" }}>
          <MagnifyingGlass size={14} weight="bold" style={{ color: "var(--omh-text-muted)" }} />
          <input
            className="jde-input"
            placeholder={searchPlaceholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ width: 280 }}
            data-testid={testid ? `${testid}-search` : "grid-search"}
          />
        </div>

        {dateCols.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flex: "0 0 auto" }}
               data-testid={testid ? `${testid}-datefilter` : "grid-datefilter"}>
            <CalendarBlank size={14} weight="bold" style={{ color: "var(--omh-text-muted)" }} />
            <select className="jde-input" value={dateField} onChange={(e) => setDateField(e.target.value)}
                    data-testid={testid ? `${testid}-datefield` : "grid-datefield"} style={{ width: 130 }}>
              {dateCols.map((c) => <option key={c.key} value={c.key}>{c.header}</option>)}
            </select>
            <input type="date" className="jde-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                   title="From date" data-testid={testid ? `${testid}-datefrom` : "grid-datefrom"} style={{ width: 140 }} />
            <span className="muted" style={{ fontSize: 11 }}>→</span>
            <input type="date" className="jde-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                   title="To date" data-testid={testid ? `${testid}-dateto` : "grid-dateto"} style={{ width: 140 }} />
            {dateActive && (
              <button type="button" className="jde-btn jde-btn-sm" onClick={clearDates}
                      data-testid={testid ? `${testid}-dateclear` : "grid-dateclear"} title="Clear dates">
                <X size={11} weight="bold" />
              </button>
            )}
          </div>
        )}

        {toolbarRight}
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--omh-text-muted)", fontWeight: 600 }}
             data-testid={testid ? `${testid}-count` : "grid-count"}>
          {(() => {
            const total = rows.length;
            const matches = sorted.length;
            if (matches === 0) return "No rows";
            const start = pageSafe * (pageSize || matches) + 1;
            const end = pageSize ? Math.min(matches, (pageSafe + 1) * pageSize) : matches;
            const showing = (pageSize && matches > pageSize)
              ? `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${matches.toLocaleString()}`
              : `${matches.toLocaleString()} ${matches === 1 ? "row" : "rows"}`;
            return showing + (matches < total ? ` · filtered from ${total.toLocaleString()}` : "");
          })()}
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="jde-grid" data-testid={testid}>
          <thead>
            <tr>
              {columns.map((c) => {
                const active = sort.key === c.key;
                const cursor = c.sortable === false ? "default" : "pointer";
                return (
                  <th
                    key={c.key}
                    onClick={() => toggle(c)}
                    style={{
                      cursor,
                      textAlign: c.align === "right" ? "right" : c.align === "center" ? "center" : "left",
                      width: c.width,
                      userSelect: "none",
                    }}
                    data-testid={testid ? `${testid}-sort-${c.key}` : `sort-${c.key}`}
                  >
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      color: active ? "var(--omh-primary)" : undefined,
                    }}>
                      {c.header}
                      {c.sortable !== false && (
                        active
                          ? (sort.dir === "asc"
                              ? <CaretUp size={10} weight="bold" />
                              : <CaretDown size={10} weight="bold" />)
                          : <CaretDown size={9} weight="regular" style={{ opacity: 0.25 }} />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => {
              const rk = getRowKey(r);
              const isExpandable = !!renderExpanded;
              const isOpen = isExpandable && expanded.has(rk);
              const isHighlight = highlightKey != null && String(rk) === String(highlightKey);
              const primaryAction = isExpandable
                ? () => toggleExpand(rk)
                : (onRowClick ? () => onRowClick(r) : undefined);
              const isClickable = !!primaryAction;
              const handleClick = isClickable
                ? (e) => {
                    // A tap we already handled via touch events fires a ghost click — ignore it.
                    if (_suppressClickRef.current) { _suppressClickRef.current = false; return; }
                    if (!activateRow(e, r)) primaryAction();
                  }
                : undefined;
              const handleTouchStart = isClickable
                ? (e) => {
                    const t = e.touches[0];
                    _touchRef.current = t ? { x: t.clientX, y: t.clientY, t: Date.now() } : null;
                  }
                : undefined;
              const handleTouchEnd = isClickable
                ? (e) => {
                    const s = _touchRef.current;
                    _touchRef.current = null;
                    if (!s) return;
                    const t = e.changedTouches[0];
                    if (!t) return;
                    const moved = Math.abs(t.clientX - s.x) + Math.abs(t.clientY - s.y);
                    const dur = Date.now() - s.t;
                    // A genuine tap: little movement, short duration. Fire the row action and
                    // cancel the browser's follow-up ghost click so we don't act twice.
                    if (moved < 12 && dur < 600) {
                      _suppressClickRef.current = true;
                      primaryAction();
                      // Guard: clear the suppress flag if no ghost click arrives.
                      setTimeout(() => { _suppressClickRef.current = false; }, 500);
                    }
                  }
                : undefined;
              return (
                <Fragment key={rk}>
                  <tr
                    ref={isHighlight ? _hlRef : undefined}
                    onClick={handleClick}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    onAuxClick={(getRowHref && (handleClick || onRowClick)) ? (e) => { if (e.button === 1) activateRow(e, r); } : undefined}
                    data-testid={testid ? `${testid}-row-${rk}` : undefined}
                    className={[getRowClassName ? getRowClassName(r) : "", isHighlight ? "grid-row-highlight" : ""].filter(Boolean).join(" ") || undefined}
                    style={{
                      cursor: handleClick ? "pointer" : "default",
                      background: isHighlight ? "var(--omh-highlight, #FEF9C3)" : (isOpen ? "var(--jde-row-hover, #F1F5F9)" : undefined),
                      boxShadow: isHighlight ? "inset 3px 0 0 var(--omh-primary, #2A5C9B)" : undefined,
                    }}
                  >
                    {columns.map((c, ci) => (
                      <td
                        key={c.key}
                        className={c.align === "right" ? "num" : ""}
                        style={c.align === "center" ? { textAlign: "center" } : undefined}
                      >
                        {ci === 0 && isExpandable && (
                          <CaretRight size={11} weight="bold" style={{ marginRight: 5, verticalAlign: "-1px", transition: "transform 0.15s ease", transform: isOpen ? "rotate(90deg)" : "none", opacity: 0.6 }} />
                        )}
                        {c.render ? c.render(r) : ((c.accessor ? c.accessor(r) : r[c.key]) ?? <span className="muted">—</span>)}
                      </td>
                    ))}
                  </tr>
                  {isOpen && (
                    <tr data-testid={testid ? `${testid}-expanded-${rk}` : undefined}>
                      <td colSpan={columns.length} style={{ padding: 0, background: "#F8FAFC", borderBottom: "2px solid var(--jde-border)" }}>
                        {renderExpanded(r)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: "center", padding: 26 }} className="muted">
                  {emptyMessage || (q ? `No rows match "${q}"` : "No data")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pageSize && sorted.length > pageSize && (
        <div className="jde-status-bar" data-testid={testid ? `${testid}-pager` : undefined}
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <span className="muted" style={{ fontSize: 12 }}>
            Showing {pageSafe * pageSize + 1}–{Math.min((pageSafe + 1) * pageSize, sorted.length)} of {sorted.length.toLocaleString()}
            {sorted.length < rows.length ? ` · filtered from ${rows.length.toLocaleString()}` : ""}
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button className="jde-btn jde-btn-sm" data-testid={testid ? `${testid}-prev` : undefined}
              disabled={pageSafe <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Prev</button>
            <span style={{ fontSize: 12, fontWeight: 600, minWidth: 92, textAlign: "center" }}>Page {pageSafe + 1} / {pageCount}</span>
            <button className="jde-btn jde-btn-sm" data-testid={testid ? `${testid}-next` : undefined}
              disabled={pageSafe >= pageCount - 1} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}>Next</button>
          </span>
        </div>
      )}

      {statusBar && <div className="jde-status-bar">{statusBar(sorted, rows)}</div>}
    </>
  );
}
